import { Button, List, Upload } from 'antd';
import type { UploadProps } from 'antd';
import { Stack, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import Swal from 'sweetalert2';
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_MO, isFileTooLarge } from './kycDocumentsApi';

export interface StagedFile {
  uid: string;
  file: File;
}

interface StagedDocumentsPickerProps {
  files: StagedFile[];
  onChange: (files: StagedFile[]) => void;
}

// Sélection de documents AVANT la création de la demande : le backend exige un
// IdDemande (qui n'existe qu'une fois la demande créée), donc on garde
// simplement les fichiers en mémoire ici, et l'appelant les envoie un par un
// juste après la création réussie (voir kycDocumentsApi.uploadKycDocument).
export default function StagedDocumentsPicker({ files, onChange }: StagedDocumentsPickerProps) {
  function addFile(file: File) {
    if (isFileTooLarge(file)) {
      Swal.fire({ title: 'Fichier trop volumineux', text: `La taille maximale est de ${DOCUMENT_MAX_MO} Mo.`, icon: 'warning' });
      return;
    }
    onChange([...files, { uid: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file }]);
  }

  function removeFile(uid: string) {
    onChange(files.filter((f) => f.uid !== uid));
  }

  return (
    <div>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AttachFileIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase' }}>
            Pièces jointes
          </Typography>
        </Stack>
        <Upload
          accept={DOCUMENT_ACCEPT}
          showUploadList={false}
          multiple
          beforeUpload={((file: File) => { addFile(file); return false; }) as UploadProps['beforeUpload']}
        >
          <Button size="small" icon={<UploadFileIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4 }} />}>
            Ajouter un document
          </Button>
        </Upload>
      </Stack>
      {files.length > 0 ? (
        <List
          size="small"
          bordered
          dataSource={files}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button key="remove" size="small" danger type="text" icon={<DeleteIcon style={{ fontSize: 14 }} />} onClick={() => removeFile(item.uid)} />,
              ]}
            >
              {item.file.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({Math.round(item.file.size / 1024)} Ko)
              </Typography>
            </List.Item>
          )}
        />
      ) : (
        <Typography variant="caption" color="text.secondary">
          Optionnel — les documents seront envoyés dès la soumission de la demande.
        </Typography>
      )}
    </div>
  );
}
